import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AIUsageBar from '../components/AIUsageBar';
import { Sparkles, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WorksheetGenerator() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [usageInfo, setUsageInfo] = useState(null);
  const [user, setUser] = useState(null);

  const checkAuthentication = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setUsageInfo({
        used: response.data.ai_generations_used || 0,
        limit: response.data.subscription_tier === 'free' ? 5 : 200,
        tier: response.data.subscription_tier || 'free'
      });
      setCheckingAuth(false);
    } catch (error) {
      toast.error('Please sign in to use AI tools');
      navigate('/login?redirect=/ai-tools/worksheet-generator');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  const [formData, setFormData] = useState({
    curriculum: 'Cambridge',
    grade: 'Year 4',
    subject: 'Mathematics',
    topic: '',
    resourceType: 'Worksheet',
    difficulty: 'Mixed',
    numberOfQuestions: '10',
    questionTypes: [],
    includeAnswerKey: 'Yes',
    includeIllustrations: 'No',
    downloadFormat: 'PDF'
  });

  const questionTypes = ['MCQ', 'Fill in the blanks', 'Match', 'True / False', 'Short Answer', 'Word Problems', 'HOTS'];

  const handleQuestionTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      questionTypes: prev.questionTypes.includes(type)
        ? prev.questionTypes.filter(t => t !== type)
        : [...prev.questionTypes, type]
    }));
  };

  const handleGenerate = async () => {
    if (!formData.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    if (formData.questionTypes.length === 0) {
      toast.error('Please select at least one question type');
      return;
    }

    setGenerating(true);

    try {
      const prompt = `Generate a ${formData.resourceType} for ${formData.curriculum} curriculum, ${formData.grade}, ${formData.subject}.

Topic: ${formData.topic}
Difficulty: ${formData.difficulty}
Number of Questions: ${formData.numberOfQuestions}
Question Types: ${formData.questionTypes.join(', ')}

Create a professional ${formData.resourceType.toLowerCase()} with:
- Clear instructions
- Well-formatted questions
- Appropriate marks per question
- Proper spacing for student answers
${formData.includeAnswerKey === 'Yes' ? '- Detailed answer key' : ''}

Format it professionally for printing.`;

      const response = await axios.post(
        `${API}/ai/generate-worksheet`,
        { 
          prompt, 
          grade: formData.grade, 
          subject: formData.subject 
        },
        { withCredentials: true }
      );

      setGeneratedContent(response.data.content);
      toast.success('Worksheet generated successfully!');
      
      // Refresh auth to update usage
      checkAuthentication();
    } catch (error) {
      console.error('Worksheet generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate worksheet');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.post(
        `${API}/download/worksheet`,
        { 
          content: generatedContent,
          format: formData.downloadFormat.toLowerCase()
        },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `worksheet-${formData.topic.replace(/\s+/g, '-').toLowerCase()}.${formData.downloadFormat.toLowerCase()}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Worksheet downloaded as ${formData.downloadFormat}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download worksheet');
    }
  };

  if (generatedContent) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <section className="py-16 px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-slate-900 mb-8">Your Generated Resource</h1>
            <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm mb-6 worksheet-content">
              {/* Render structured worksheet content */}
              {generatedContent.header && (
                <div className="mb-6 pb-6 border-b">
                  <h2 className="text-2xl font-bold text-slate-900">{generatedContent.header.title}</h2>
                  {generatedContent.header.subtitle && (
                    <p className="text-slate-600 mt-1">{generatedContent.header.subtitle}</p>
                  )}
                </div>
              )}
              
              {generatedContent.learning_objectives && generatedContent.learning_objectives.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-slate-900 mb-2">Learning Objectives:</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    {generatedContent.learning_objectives.map((obj, i) => (
                      <li key={i}>{typeof obj === 'object' ? obj.text : obj}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {generatedContent.sections && generatedContent.sections.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <h3 className="font-bold text-lg text-slate-900 mb-3">{section.title}</h3>
                  {section.questions && section.questions.map((q, qIdx) => (
                    <div key={qIdx} className="mb-4 pl-4">
                      <p className="font-semibold text-slate-800 mb-1">
                        {q.question_number}. {q.question_text}
                      </p>
                      {q.options && q.options.length > 0 && (
                        <ul className="list-none ml-4 space-y-1">
                          {q.options.map((opt, oIdx) => (
                            <li key={oIdx} className="text-slate-600">{opt}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              
              {generatedContent.answer_key && (
                <div className="mt-8 pt-6 border-t">
                  <h3 className="font-bold text-lg text-slate-900 mb-3">Answer Key</h3>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    {typeof generatedContent.answer_key === 'string' ? (
                      <p className="text-slate-700 whitespace-pre-wrap">{generatedContent.answer_key}</p>
                    ) : (
                      generatedContent.answer_key.map((answer, i) => (
                        <p key={i} className="text-slate-700">{answer}</p>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              {/* Fallback for plain text content */}
              {!generatedContent.sections && typeof generatedContent === 'string' && (
                <div className="prose max-w-none whitespace-pre-wrap">{generatedContent}</div>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setGeneratedContent(null)}
                className="flex-1 bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-3 rounded-xl font-semibold"
              >
                Generate Another
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download {formData.downloadFormat}
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">AI Worksheet Generator</h1>
            <p className="text-base text-slate-600">Create custom worksheets with answer keys</p>
          </div>

          {/* Usage Bar */}
          {usageInfo && (
            <div className="mb-8">
              <AIUsageBar 
                used={usageInfo.used} 
                limit={usageInfo.limit} 
                tier={usageInfo.tier} 
              />
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm space-y-6">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Curriculum</label>
                <select
                  value={formData.curriculum}
                  onChange={(e) => setFormData({ ...formData, curriculum: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>Cambridge</option>
                  <option>IB</option>
                  <option>Oxford</option>
                  <option>National Curriculum</option>
                  <option>Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Year / Grade</label>
                <select
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>Year 2</option>
                  <option>Year 3</option>
                  <option>Year 4</option>
                  <option>Year 5</option>
                  <option>Year 6</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Subject</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>Mathematics</option>
                  <option>Science</option>
                  <option>English</option>
                  <option>Social Studies</option>
                  <option>General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Resource Type</label>
                <select
                  value={formData.resourceType}
                  onChange={(e) => setFormData({ ...formData, resourceType: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>Worksheet</option>
                  <option>Practice Sheet</option>
                  <option>Homework</option>
                </select>
              </div>
            </div>

            {/* Topic */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Topic *</label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                placeholder="e.g., Solar System, Fractions, Grammar"
                className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
              />
            </div>

            {/* Worksheet Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Difficulty</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>Build</option>
                  <option>Grow</option>
                  <option>Shine</option>
                  <option>Mixed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Number of Questions</label>
                <input
                  type="number"
                  value={formData.numberOfQuestions}
                  onChange={(e) => setFormData({ ...formData, numberOfQuestions: e.target.value })}
                  min="5"
                  max="30"
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                />
              </div>
            </div>

            {/* Question Types */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">Question Types *</label>
              <div className="grid md:grid-cols-2 gap-3">
                {questionTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => handleQuestionTypeToggle(type)}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      formData.questionTypes.includes(type)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-slate-200 hover:border-purple-300'
                    }`}
                  >
                    <span className="font-semibold text-slate-900">{type}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Options */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Include Answer Key</label>
                <select
                  value={formData.includeAnswerKey}
                  onChange={(e) => setFormData({ ...formData, includeAnswerKey: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>Yes</option>
                  <option>No</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Include Illustrations</label>
                <select
                  value={formData.includeIllustrations}
                  onChange={(e) => setFormData({ ...formData, includeIllustrations: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>No</option>
                  <option>Yes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Download Format</label>
                <select
                  value={formData.downloadFormat}
                  onChange={(e) => setFormData({ ...formData, downloadFormat: e.target.value })}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                >
                  <option>PDF</option>
                  <option>DOCX</option>
                </select>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-worksheet-button"
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl px-8 py-4 font-semibold shadow-sm hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating Worksheet...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Worksheet
                </>
              )}
            </button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default WorksheetGenerator;
